/**
 * สิ่งที่ต้องทำ (พนักงานใหม่) — เช็คลิสต์ onboarding ของตัวเอง: เอกสารที่ต้องส่ง /
 * เอกสารที่ต้องเซ็น (ลิงก์ไป "เอกสารของฉัน") + สิ่งที่ HR ตั้งค่าให้ (อ่านอย่างเดียว).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/layout/AdminLayout';
import { useMyOnboarding } from '../hooks';
import { ONBOARDING_CATEGORY_LABELS, type OnboardingCategory } from '@payroll/shared';
import { CheckCircle2, Circle, Upload, PenLine } from 'lucide-react';

export function MyOnboarding() {
  const { data, isLoading } = useMyOnboarding();

  const grouped = useMemo(() => {
    const byCat = new Map<OnboardingCategory, NonNullable<typeof data>['items']>();
    for (const it of data?.items ?? []) {
      const list = byCat.get(it.category) ?? [];
      list.push(it);
      byCat.set(it.category, list);
    }
    return byCat;
  }, [data]);

  const required = data?.items.filter((i) => i.required) ?? [];
  const doneCount = required.filter((i) => i.done).length;

  return (
    <AdminLayout title="สิ่งที่ต้องทำ (พนักงานใหม่)">
      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : !data || data.status !== 'active' ? (
        <p className="text-sm text-gray-400 italic">ไม่มีรายการรับพนักงานใหม่ที่ต้องทำในตอนนี้</p>
      ) : (
        <>
          <div className="card p-3 mb-4">
            <div className="text-sm text-gray-600 mb-1">ความคืบหน้า</div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
              <div
                className="h-full bg-brand-500"
                style={{ width: `${required.length > 0 ? Math.round((doneCount / required.length) * 100) : 100}%` }}
              />
            </div>
            <div className="text-xs text-gray-400">
              {doneCount}/{required.length} รายการที่จำเป็น
            </div>
          </div>

          <div className="space-y-4">
            {[...grouped.entries()].map(([cat, items]) => (
              <div key={cat}>
                <div className="text-xs font-semibold text-gray-500 mb-1">{ONBOARDING_CATEGORY_LABELS[cat]}</div>
                <div className="space-y-1">
                  {(items ?? []).map((it) => (
                    <div
                      key={it.item_key}
                      className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {it.done ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        )}
                        <span className="truncate">
                          {it.label} {!it.required && <span className="text-gray-400 text-xs">(ไม่บังคับ)</span>}
                        </span>
                      </div>
                      {!it.done && (cat === 'document' || cat === 'signature') && (
                        <Link
                          to="/my-documents"
                          className="text-xs text-brand-600 hover:underline flex-shrink-0 flex items-center gap-1"
                        >
                          {cat === 'signature' ? <PenLine className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                          {cat === 'signature' ? 'ไปเซ็น' : 'ไปอัปโหลด'}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
